/**
 * CC1Entry — Compat flow entry screen.
 * data-screen-label="cc-01 Compat entry"
 *
 * - Username input with @ strip (lowercased, max 32 chars)
 * - "Continue" disabled when empty
 * - Recent couples from GET /api/v1/me/couples (TanStack Query)
 * - Sends SEARCH event to useCompatFlow on submit / friend pick
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { useQuery } from '@tanstack/react-query';
import { normalizeUsername, isValidUsername } from '@wds/astro-bot-shared/validation/username';
import { apiFetch } from '../../../lib/api-client';
import { useAuth } from '../../../store/auth';
import { CCBackdrop, CCStars } from './shared';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import type { UseCompatFlowReturn } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

interface CoupleItem {
  userId: string;
  displayName: string;
  archetype: string;
  completedAt: string;
  username?: string;
}

interface CouplesResponse {
  items: CoupleItem[];
  nextCursor: string | null;
}

// Tint colors for recent friend avatars — cycle through 3
const FRIEND_TINTS = ['#c89cff', '#90caf9', '#ff8eb0'];

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC1EntryProps {
  flow: UseCompatFlowReturn;
}

export function CC1Entry({ flow }: CC1EntryProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['flows', 'common']);
  const jwt = useAuth((s) => s.jwt);
  const [username, setUsername] = useState('');

  // Fetch recent couples (top 3)
  const { data: couplesData } = useQuery<CouplesResponse>({
    queryKey: ['me', 'couples', 'recent'],
    queryFn: async () => {
      const res = await apiFetch('/api/v1/me/couples', jwt ?? '', {
        headers: { 'content-type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to fetch couples');
      return (await res.json()) as CouplesResponse;
    },
    enabled: !!jwt,
    staleTime: 60_000,
  });

  const recentFriends = (couplesData?.items ?? []).slice(0, 3);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/^@+/, '').toLowerCase().slice(0, 32);
    setUsername(raw);
  }

  function handleSubmit() {
    const normalized = normalizeUsername(username);
    if (!normalized) return;
    flow.send({ type: 'SEARCH', username: normalized });
  }

  function handleFriendPick(friend: CoupleItem) {
    const uname = friend.username ?? friend.displayName.toLowerCase().replace(/\s+/g, '');
    flow.send({ type: 'SEARCH', username: uname });
  }

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      void navigate('/catalog');
    }
  }

  // Valid Telegram handle (5–32 chars of [a-z0-9_]) per the shared validator —
  // matches the bot + invite-route rules so the three no longer diverge.
  const canSubmit = isValidUsername(username);

  return (
    <div className={styles.root} data-screen-label="cc-01 Compat entry">
      <CCBackdrop tone="quiet" />
      <CCStars seed={1} count={30} />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('flows:compat.headerTitle')} onBack={handleBack} headingId="compat-heading" />

        {/* Header copy */}
        <div style={{ marginTop: 18 }}>
          <div className={styles.eyebrow}>{t('compat.entry.eyebrow', { stars: SKU_CATALOG['compat_check'].stars })}</div>
          <h1 className={styles.displayTitle}>
            {t('compat.entry.titlePrefix')}
            <em className={styles.gradAccent}>{t('compat.entry.titleAccent')}</em>{t('compat.entry.titleSuffix')}
          </h1>
          <p className={styles.bodyText}>
            {t('compat.entry.body')}
          </p>
        </div>

        {/* Username input */}
        <div className={styles.inputWrap}>
          <div className={styles.inputLabel}>{t('compat.entry.inputLabel')}</div>
          <div className={styles.inputRow}>
            <span className={styles.inputAt}>@</span>
            <input
              className={styles.inputField}
              type="text"
              value={username}
              onChange={handleInput}
              placeholder={t('compat.entry.inputPlaceholder')}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={32}
              aria-label={t('compat.entry.inputAria')}
            />
          </div>
          <div className={styles.inputHint}>
            {t('compat.entry.hintPrefix')}
            <span style={{ color: 'var(--gold-bright)' }}>calestea.com/c/&lt;name&gt;</span>
            {t('compat.entry.hintSuffix')}
          </div>
        </div>

        {/* Recent friends */}
        <div style={{ marginTop: 24 }}>
          <div className={styles.recentHeader}>
            <span className={styles.recentLabel}>{t('compat.entry.recentLabel')}</span>
            <span className={styles.recentRule} />
          </div>

          {recentFriends.length > 0 ? (
            recentFriends.map((friend, i) => (
              <button
                key={friend.userId}
                type="button"
                className={styles.friendBtn}
                onClick={() => handleFriendPick(friend)}
                style={{ marginTop: i > 0 ? 8 : 0 }}
              >
                <div
                  className={styles.friendAvatar}
                  style={{
                    background: `radial-gradient(circle at 35% 35%, ${FRIEND_TINTS[i % FRIEND_TINTS.length]}, #1a1340)`,
                    border: `0.5px solid ${FRIEND_TINTS[i % FRIEND_TINTS.length]}`,
                  }}
                >
                  {friend.displayName[0].toUpperCase()}
                </div>
                <div>
                  <div className={styles.friendName}>{friend.displayName}</div>
                  <div className={styles.friendArch}>{friend.archetype}</div>
                </div>
                <span className={styles.friendPick}>{t('compat.entry.pick')} ›</span>
              </button>
            ))
          ) : (
            // Skeleton placeholders while loading or no data
            ['', '', ''].map((_, i) => (
              <div
                key={i}
                className={styles.friendBtn}
                style={{
                  marginTop: i > 0 ? 8 : 0,
                  opacity: 0.35,
                  pointerEvents: 'none',
                  minHeight: 56,
                }}
                aria-hidden="true"
              />
            ))
          )}
        </div>

        {/* CTA */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            data-testid="cc1-continue-btn"
          >
            {t('common:actions.continue')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
